import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router";
import { Avatar, Button, Col, Row, Skeleton, Spin, Tooltip } from "antd";
import Title from "antd/lib/typography/Title";
import { doc, onSnapshot } from "firebase/firestore";
import AddPortfolio from "Components/Forms/AddPortfolio";
import OverviewCard from "Components/Dashboard/OverviewCard";
import { getCoinInfo } from "utils/CoinGecko";
import { getIdentityUser } from "utils/Identity";
import { db } from "utils/Firebase/init";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import {
    BarChartOutlined,
    LoadingOutlined,
    PlusOutlined,
    ReloadOutlined,
} from "@ant-design/icons";
import InvestmentList from "Components/Drawers/InvestmentList";
import { getFormattedAmount } from "Shared/Utils";
import getCurrencySymbol from "Shared/CurrancySymbol";
import { PerformanceTag } from "Shared/PerformanceTag";
import { Tokens } from "Shared/Tokens";
import { orderBy } from "lodash";
import Settings from "Components/Drawers/Settings";
import { fetchBackup, getBackupDocId } from "utils/Firebase/backup";
import {
    runWithCloudHydrationGuard,
    shouldIgnoreCloudSnapshot,
} from "utils/Firebase/autoSync";
import AppShell, { AutoSyncBadge } from "Components/Layout/AppShell";
import {
    setSelectedPortfolio,
    updateBackedupPortfolios,
} from "store/Actions/portfolio";
import { updateSettings } from "store/Actions/settings";
import { updateBackedupAssets } from "store/Actions/asset";
import { updateUser } from "store/Actions/users";
import {
    mpTapCreatePortfolioCTA,
    mpTapInvestmentListViewCTA,
    mpTapRefreshCTA,
    mpTapSettingsCTA,
    mpTapViewMoreCTA,
} from "Shared/Mixpanel/Portfolios";
import ImportedPortfolioCard from "Components/Dashboard/ImportedPortfolioCard";
import LoginWall from "Components/Shared/LoginWall";
import { version as currentVersion } from "../../../package.json";

const computeAssetPositionStats = (asset, coinInfo) => {
    const txList = Array.isArray(asset?.transactions) ? asset.transactions : [];
    const numberOfCoins = Number(asset?.portfolio?.latest?.numberOfCoins) || 0;
    const isExited = numberOfCoins === 0 && txList.length > 0;
    const investment =
        txList.length > 0
            ? Number(asset?.portfolio?.latest?.investment) || 0
            : 0;
    const livePrice =
        Number(coinInfo?.current_price) ||
        Number(asset?.portfolio?.latest?.marketPrice) ||
        0;
    const currentValue = numberOfCoins > 0 ? livePrice * numberOfCoins : 0;
    const plValue = currentValue - investment;
    const plPct = investment > 0 ? (plValue / investment) * 100 : 0;

    let lifetimeBought = 0;
    let realizedProceeds = 0;
    let realizedCostBasis = 0;

    txList.forEach((tx) => {
        const snap = tx?.portfolioSnapshot || {};
        const prevInv = Number(snap?.previous?.investment) || 0;
        const latestInv = Number(snap?.latest?.investment) || 0;
        const prevCoins = Number(snap?.previous?.numberOfCoins) || 0;
        const latestCoins = Number(snap?.latest?.numberOfCoins) || 0;
        const txPrice = Number(snap?.transactionMarketPrice) || 0;

        if (tx?.type === "buy" || latestInv > prevInv) {
            lifetimeBought += Math.max(0, latestInv - prevInv);
        } else if (tx?.type === "sell" || prevCoins > latestCoins) {
            const soldProceeds =
                Number(snap?.latest?.sellInfo?.amountToSell) ||
                Math.max(0, (prevCoins - latestCoins) * txPrice);
            const soldBasis =
                Number(snap?.latest?.sellInfo?.intialCapitalForPurchase) ||
                Math.max(0, prevInv - latestInv);
            realizedProceeds += soldProceeds;
            realizedCostBasis += soldBasis;
        }
    });

    if (lifetimeBought === 0 && txList.length > 0) {
        lifetimeBought =
            Number(asset?.portfolio?.previous?.investment) ||
            Number(
                asset?.portfolio?.latest?.sellInfo?.intialCapitalForPurchase
            ) ||
            investment;
    }
    if (realizedProceeds === 0 && isExited) {
        realizedProceeds =
            Number(asset?.portfolio?.latest?.sellInfo?.amountToSell) || 0;
    }
    if (realizedCostBasis === 0 && isExited) {
        realizedCostBasis = lifetimeBought;
    }

    const realizedPl = realizedProceeds - realizedCostBasis;
    const realizedPlPct =
        realizedCostBasis > 0 ? (realizedPl / realizedCostBasis) * 100 : 0;

    return {
        txList,
        numberOfCoins,
        isExited,
        investment,
        currentValue,
        plValue,
        plPct,
        lifetimeBought,
        realizedProceeds,
        realizedPl,
        realizedPlPct,
    };
};

const Portfolios = () => {
    dayjs.extend(duration);
    const history = useHistory();
    const { search } = useLocation();
    const login = new URLSearchParams(search).get("login");
    const dispatch = useDispatch();
    const portfolios = useSelector((state) => state.portfolios.portfolios);
    const assets = useSelector((state) => state.assets.assets);
    const settings = useSelector((state) => state.settings);
    const displayCurrency = settings?.currency || "INR";
    const currSymbol = getCurrencySymbol(displayCurrency);

    const [showAddPortfolioDrawer, setShowAddPortfolioDrawer] = useState(false);
    const [items, setItems] = useState({
        newPortfolios: [],
        activePortfolios: [],
        inactivePortfolios: [],
    });
    const [headerStats, setHeaderStats] = useState({
        currentValue: 0,
        totalInvestment: 0,
        pl: 0,
    });
    const [refreshing, setRefreshing] = useState(false);
    const [showInvestmentsDrawer, setShowInvestmentsDrawer] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [investments, setInvestments] = useState([]);
    const [marketDataCache, setMarketDataCache] = useState([]);
    const userInfo = useSelector((state) => state.users.userInfo);
    const [showImportedPortfolios, setShowImportedPortfolios] = useState(false);
    const [updateAvailableVersion, setUpdateAvailableVersion] = useState(null);

    const handleApplyAppUpdate = async () => {
        try {
            if (
                typeof navigator !== "undefined" &&
                navigator.serviceWorker &&
                typeof navigator.serviceWorker.getRegistrations === "function"
            ) {
                const registrations =
                    await navigator.serviceWorker.getRegistrations();
                await Promise.all(registrations.map((reg) => reg.unregister()));
            }
            if (
                typeof window !== "undefined" &&
                window.caches &&
                typeof window.caches.keys === "function"
            ) {
                const keys = await window.caches.keys();
                await Promise.all(keys.map((key) => window.caches.delete(key)));
            }
        } catch (err) {
            // ignore cleanup error
        }
        window.location.reload();
    };

    const restorePortfolio = (backedUpPortfolios) => {
        dispatch(updateBackedupPortfolios(backedUpPortfolios));
    };

    const handleSettingsDrawer = () => {
        if (history && typeof history.push === "function") {
            history.push("/settings");
        }
    };

    const handleMoreClk = (uid) => {
        const portfolio = portfolios.find((item) => item.uid === uid);
        mpTapViewMoreCTA({
            name: portfolio.name,
        });
        dispatch(setSelectedPortfolio(portfolio));
        history.push(`/portfolios/${portfolio.name}`);
    };

    const handleBinanceDrawer = () => {
        history.push(`/imported/binance`);
    };

    const getTotalInvestmentList = async (coinsMarketDataInput) => {
        const symbol = getCurrencySymbol(displayCurrency);
        const marketList = Array.isArray(coinsMarketDataInput)
            ? coinsMarketDataInput
            : marketDataCache;
        const list = portfolios.map((portfolio) => {
            const currentPortfolioAssets = assets.filter(
                (asset) => asset.portfolioId === portfolio.uid
            );
            const updatedAssets = currentPortfolioAssets.map((asset) => {
                const coinInfo = Array.isArray(marketList)
                    ? marketList.find((data) => data.id === asset.id)
                    : null;
                const stats = computeAssetPositionStats(asset, coinInfo);
                const displayInvested = stats.isExited
                    ? stats.lifetimeBought
                    : stats.investment;

                return {
                    name: asset.name,
                    key: asset.uid,
                    uid: asset.uid,
                    id: asset.id,
                    isExited: stats.isExited,
                    numberOfCoins: stats.numberOfCoins,
                    amount: displayInvested,
                    investment: stats.investment,
                    currentValue: stats.currentValue,
                    pl: stats.plPct,
                    plValue: stats.plValue,
                    plPct: stats.plPct,
                    lifetimeBought: stats.lifetimeBought,
                    realizedProceeds: stats.realizedProceeds,
                    realizedPl: stats.realizedPl,
                    realizedPlPct: stats.realizedPlPct,
                    portfolio: asset.portfolio,
                    transactions: stats.txList,
                    amountView: (
                        <React.Fragment>
                            {symbol}&nbsp;
                            {getFormattedAmount(displayInvested)}
                        </React.Fragment>
                    ),
                };
            });
            return {
                portfolio: portfolio.name,
                uid: portfolio.uid,
                assets: updatedAssets.sort((a, b) => {
                    if (a.isExited !== b.isExited) {
                        return a.isExited ? 1 : -1;
                    }
                    return (b.amount || 0) - (a.amount || 0);
                }),
            };
        });
        setInvestments(list.filter((l) => l.assets.length > 0));
    };

    const getAllPortfolioAssetsInfo = async () => {
        setRefreshing(true);
        const _headerStats = {
            currentValue: 0,
            totalInvestment: 0,
            pl: 0,
        };
        const coinsMarketData =
            (await getCoinInfo(assets, displayCurrency)) || [];
        setMarketDataCache(coinsMarketData);
        const _portfolios = portfolios.map(async (portfolio) => {
            let currentValue = 0;
            let totalInvestment = 0;
            let totalCoins = 0;
            let pl = 0;
            let activeCount = 0;
            let exitedCount = 0;
            const currentPortfolioAssets = assets.filter(
                (asset) => asset.portfolioId === portfolio.uid
            );
            currentPortfolioAssets.forEach((asset) => {
                const txCount = Array.isArray(asset.transactions)
                    ? asset.transactions.length
                    : 0;
                const numCoins =
                    Number(asset.portfolio?.latest?.numberOfCoins) || 0;
                if (numCoins > 0) {
                    activeCount += 1;
                } else if (numCoins === 0 && txCount > 0) {
                    exitedCount += 1;
                }
            });
            const assetSummary = `${activeCount} active${
                exitedCount > 0 ? ` · ${exitedCount} exited` : ""
            }`;
            const allCoins = currentPortfolioAssets.map((asset) => {
                if (asset.transactions.length > 0) {
                    const coinInfo = coinsMarketData.find(
                        (data) => data.id === asset.id
                    );
                    return {
                        ...asset,
                        coinInfo,
                    };
                }
                return asset;
            });
            const coins = allCoins.filter((coin) => coin.coinInfo);
            coins.forEach((coin) => {
                currentValue +=
                    coin.transactions.length > 0
                        ? coin.coinInfo.current_price *
                          coin.portfolio.latest.numberOfCoins
                        : 0;
                totalInvestment +=
                    coin.transactions.length > 0
                        ? coin.portfolio.latest.investment
                        : 0;
                totalCoins +=
                    coin.transactions.length > 0
                        ? Number(coin.portfolio.latest.numberOfCoins) || 0
                        : 0;
            });
            _headerStats.currentValue += currentValue;
            _headerStats.totalInvestment += totalInvestment;
            pl =
                totalInvestment !== 0
                    ? ((currentValue - totalInvestment) / totalInvestment) * 100
                    : 0;
            const avgPrice = totalCoins > 0 ? totalInvestment / totalCoins : 0;
            return {
                ...portfolio,
                totalInvestment,
                currentValue,
                avgPrice,
                pl,
                activeCount,
                exitedCount,
                assetSummary,
            };
        });
        const portfoliosData = await Promise.all(_portfolios);
        _headerStats.pl =
            _headerStats.totalInvestment !== 0
                ? ((_headerStats.currentValue - _headerStats.totalInvestment) /
                      _headerStats.totalInvestment) *
                  100
                : 0;
        const activePortfolios = orderBy(
            portfoliosData.filter((portfolio) => portfolio.totalInvestment > 0),
            ["pl"],
            ["desc"]
        );
        const inactivePortfolios = orderBy(
            portfoliosData.filter(
                (portfolio) =>
                    (portfolio.createdAt
                        ? dayjs(Date.now()).diff(
                              portfolio.createdAt,
                              "days",
                              true
                          ) >= 1
                        : true) && portfolio.totalInvestment === 0
            ),
            ["createdAt"],
            "desc"
        );
        const newPortfolios = orderBy(
            portfoliosData.filter(
                (portfolio) =>
                    portfolio.createdAt &&
                    dayjs(Date.now()).diff(portfolio.createdAt, "days", true) <
                        1 &&
                    portfolio.totalInvestment === 0
            ),
            ["createdAt"],
            ["desc"]
        );
        setItems({
            newPortfolios,
            activePortfolios,
            inactivePortfolios,
        });
        setHeaderStats(_headerStats);
        getTotalInvestmentList(coinsMarketData);
        setRefreshing(false);
    };

    const handleInvestmentListDrawer = () => {
        if (showInvestmentsDrawer) {
            setShowInvestmentsDrawer(false);
        } else {
            getTotalInvestmentList();
            setShowInvestmentsDrawer(true);
        }
    };

    const getAlreadyBackedUpData = async (targetUser = userInfo) => {
        setRefreshing(true);
        if (targetUser) {
            const data = await fetchBackup("users", targetUser, {
                silent: true,
            });
            if (data) {
                runWithCloudHydrationGuard(() => {
                    restorePortfolio(data.portfolios);
                    dispatch(updateBackedupAssets(data.assets));
                    dispatch(
                        updateSettings(data.settings, { fromCloud: true })
                    );
                });
            }
        }
        setRefreshing(false);
    };

    useEffect(() => {
        getIdentityUser().then((result) => {
            if (result.status === "error") {
                return;
            }

            if (result.status === "unauthenticated") {
                setShowImportedPortfolios(false);
                if (userInfo) dispatch(updateUser(null));
                return;
            }

            const identityUser = result.user;
            if (!identityUser) return;
            setShowImportedPortfolios(true);
            const rawFullName =
                (typeof identityUser.name === "string"
                    ? identityUser.name
                    : identityUser.name?.fullName) ||
                identityUser.displayName ||
                identityUser.email ||
                "User";
            const parts = String(rawFullName).trim().split(/\s+/);
            const parsedUser = {
                id: identityUser._id || identityUser.id || identityUser.uid,
                uid:
                    identityUser.uid ||
                    identityUser.id ||
                    identityUser._id ||
                    identityUser.googleId ||
                    identityUser.email,
                email: identityUser.email || "",
                picture: identityUser.picture || identityUser.photoURL || "",
                name:
                    typeof identityUser.name === "object" &&
                    identityUser.name !== null
                        ? identityUser.name
                        : {
                              firstName: parts[0] || "User",
                              lastName: parts.slice(1).join(" ") || "",
                              fullName: rawFullName,
                          },
            };
            dispatch(updateUser(parsedUser));
            if (Number(login) === 1) {
                getAlreadyBackedUpData(parsedUser);
            }
        });
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        const docName = getBackupDocId(userInfo);
        if (!docName || typeof onSnapshot !== "function") {
            return undefined;
        }
        let unsubscribe;
        try {
            const userDocRef = doc(db, "users", docName);
            unsubscribe = onSnapshot(
                userDocRef,
                (snapshot) => {
                    if (snapshot && snapshot.exists()) {
                        const data = snapshot.data();
                        if (shouldIgnoreCloudSnapshot(snapshot, data)) {
                            return;
                        }
                        runWithCloudHydrationGuard(() => {
                            if (Array.isArray(data?.portfolios)) {
                                restorePortfolio(data.portfolios);
                            }
                            if (Array.isArray(data?.assets)) {
                                dispatch(updateBackedupAssets(data.assets));
                            }
                            if (data?.settings) {
                                dispatch(
                                    updateSettings(data.settings, {
                                        fromCloud: true,
                                    })
                                );
                            }
                        });
                    }
                },
                () => {
                    // ignore listener error
                }
            );
        } catch (e) {
            // ignore firestore setup error in test/offline environments
        }
        return () => {
            if (typeof unsubscribe === "function") {
                unsubscribe();
            }
        };
        // eslint-disable-next-line
    }, [userInfo?.uid, userInfo?.id, userInfo?.email]);

    useEffect(() => {
        setHeaderStats({
            ...headerStats,
            pl:
                headerStats.totalInvestment !== 0
                    ? ((headerStats.currentValue -
                          headerStats.totalInvestment) /
                          headerStats.totalInvestment) *
                      100
                    : 0,
        });
        // eslint-disable-next-line
    }, [headerStats.currentValue]);

    useEffect(() => {
        getAllPortfolioAssetsInfo();
        getTotalInvestmentList();
        // eslint-disable-next-line
    }, [JSON.stringify(portfolios), JSON.stringify(assets), displayCurrency]);

    useEffect(() => {
        let isMounted = true;
        const checkVersion = async () => {
            try {
                if (typeof fetch !== "function") return;
                const response = await fetch(`/version.json?t=${Date.now()}`, {
                    cache: "no-store",
                });
                if (!response || !response.ok) return;
                const res = await response.json();
                if (
                    isMounted &&
                    res &&
                    res.version &&
                    res.version !== currentVersion
                ) {
                    setUpdateAvailableVersion(res.version);
                }
            } catch (err) {
                // ignore version check errors
            }
        };
        checkVersion();
        const intervalId = setInterval(checkVersion, 5 * 60 * 1000);
        return () => {
            isMounted = false;
            clearInterval(intervalId);
        };
    }, []);

    const hasManualBalance =
        headerStats.currentValue > 0 || headerStats.totalInvestment > 0;

    let cachedBinanceSummary = null;
    try {
        const rawCache = localStorage.getItem("binance_wallet_summary_cache");
        if (rawCache) {
            cachedBinanceSummary = JSON.parse(rawCache);
        }
    } catch (e) {
        cachedBinanceSummary = null;
    }
    const binanceTotalValue = showImportedPortfolios
        ? Number(cachedBinanceSummary?.totalValue || 0)
        : 0;
    const binanceInvested = showImportedPortfolios
        ? Number(cachedBinanceSummary?.invested || 0)
        : 0;
    const combinedValue =
        (Number(headerStats.currentValue) || 0) + binanceTotalValue;
    const combinedInvested =
        (Number(headerStats.totalInvestment) || 0) + binanceInvested;
    const combinedNetPl = combinedValue - combinedInvested;
    const combinedPlPct =
        combinedInvested > 0
            ? (combinedNetPl / combinedInvested) * 100
            : Number(headerStats.pl) || 0;
    const hasCombinedBalance =
        hasManualBalance || binanceTotalValue > 0 || binanceInvested > 0;

    const allManualPortfolios = [
        ...(items.activePortfolios || []),
        ...(items.newPortfolios || []),
        ...(items.inactivePortfolios || []),
    ];
    const userInitials = (
        userInfo?.name?.fullName ||
        userInfo?.name?.firstName ||
        "U"
    )
        .trim()
        .charAt(0)
        .toUpperCase();

    return (
        <div>
            <AppShell
                activeTab="overview"
                title={
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            maxWidth: Tokens.containerMaxWidth,
                            margin: "0 auto",
                            width: "100%",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                            }}
                        >
                            <Title
                                level={4}
                                style={{
                                    marginBottom: 0,
                                    color: Tokens.textPrimary,
                                    fontWeight: 500,
                                    fontSize: "18px",
                                    display: "flex",
                                    alignItems: "center",
                                }}
                            >
                                Portfolios
                            </Title>
                            <AutoSyncBadge />
                        </div>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                            }}
                        >
                            <Button
                                type="text"
                                icon={
                                    <ReloadOutlined
                                        spin={refreshing}
                                        style={{
                                            color: Tokens.textSecondary,
                                            fontSize: "16px",
                                        }}
                                    />
                                }
                                onClick={() => {
                                    mpTapRefreshCTA();
                                    getAllPortfolioAssetsInfo();
                                }}
                                title="Refresh portfolios"
                                style={{
                                    backgroundColor: Tokens.raised,
                                    border: `1px solid ${Tokens.hairline}`,
                                    borderRadius: Tokens.radiusControl,
                                    width: 38,
                                    height: 38,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            />
                            <Tooltip
                                title={
                                    userInfo?.name?.fullName ||
                                    "Profile & settings"
                                }
                            >
                                <Avatar
                                    data-testid="open-settings-btn"
                                    size={38}
                                    src={userInfo?.picture || undefined}
                                    onClick={() => {
                                        mpTapSettingsCTA();
                                        handleSettingsDrawer();
                                    }}
                                    style={{
                                        cursor: "pointer",
                                        backgroundColor: Tokens.raised,
                                        color: Tokens.textPrimary,
                                        border: `1px solid ${Tokens.hairline}`,
                                        fontSize: "13px",
                                        fontWeight: 500,
                                    }}
                                >
                                    {!userInfo?.picture ? userInitials : null}
                                </Avatar>
                            </Tooltip>
                        </div>
                    </div>
                }
                placement="right"
                closable={false}
                push={false}
                height="100%"
                width="100%"
                open
                headerStyle={{
                    backgroundColor: Tokens.bg,
                    borderBottom: `1px solid ${Tokens.hairline}`,
                    padding: "16px 24px",
                }}
                bodyStyle={{
                    backgroundColor: Tokens.bg,
                    padding: "24px 16px 80px",
                }}
                footerStyle={{
                    backgroundColor: Tokens.card,
                    borderTop: `1px solid ${Tokens.hairline}`,
                    padding:
                        "10px 16px calc(10px + env(safe-area-inset-bottom, 0px))",
                    textAlign: "left",
                }}
                footer={
                    <div
                        style={{
                            maxWidth: Tokens.containerMaxWidth,
                            margin: "0 auto",
                            width: "100%",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: "12px",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "2px",
                                minWidth: 0,
                            }}
                        >
                            <span
                                style={{
                                    fontSize: "11px",
                                    color: Tokens.textMuted,
                                    lineHeight: 1.2,
                                }}
                            >
                                Workspace
                            </span>
                            <span
                                style={{
                                    fontSize: "13px",
                                    fontFamily: Tokens.fontMono,
                                    fontVariantNumeric: "tabular-nums",
                                    color: Tokens.textPrimary,
                                    fontWeight: 500,
                                    lineHeight: 1.3,
                                }}
                            >
                                {`${portfolios.length} ${
                                    portfolios.length === 1
                                        ? "portfolio"
                                        : "portfolios"
                                } · ${displayCurrency.toUpperCase()}`}
                            </span>
                        </div>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => {
                                mpTapCreatePortfolioCTA();
                                setShowAddPortfolioDrawer(true);
                            }}
                            style={{
                                height: "38px",
                                padding: "0 16px",
                                borderRadius: Tokens.radiusControl,
                                fontWeight: 500,
                                fontSize: "13px",
                                flexShrink: 0,
                            }}
                        >
                            Create portfolio
                        </Button>
                    </div>
                }
            >
                <div
                    style={{
                        maxWidth: Tokens.containerMaxWidth,
                        margin: "0 auto",
                        width: "100%",
                    }}
                >
                    {updateAvailableVersion && (
                        <div
                            data-testid="app-update-available-banner"
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: "12px",
                                backgroundColor: Tokens.accentSubtle,
                                border: `1px solid ${Tokens.accentBorder}`,
                                borderRadius: Tokens.radiusCard,
                                padding: "10px 14px",
                                marginBottom: "16px",
                            }}
                        >
                            <span
                                style={{
                                    color: Tokens.textPrimary,
                                    fontSize: "13px",
                                    fontWeight: 500,
                                }}
                            >
                                {`Update v${updateAvailableVersion} is available`}
                            </span>
                            <Button
                                size="small"
                                type="primary"
                                onClick={handleApplyAppUpdate}
                                style={{
                                    height: "32px",
                                    padding: "0 12px",
                                    borderRadius: Tokens.radiusControl,
                                    fontWeight: 500,
                                    fontSize: "12px",
                                    flexShrink: 0,
                                }}
                            >
                                Update now
                            </Button>
                        </div>
                    )}

                    {showImportedPortfolios && (
                        <React.Fragment>
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: "10px",
                                    marginTop: "20px",
                                }}
                            >
                                <span
                                    style={{
                                        fontSize: "13px",
                                        fontWeight: 500,
                                        color: Tokens.textSecondary,
                                    }}
                                >
                                    Connected exchanges
                                </span>
                            </div>
                            <Row
                                onClick={() => handleBinanceDrawer()}
                                style={{ cursor: "pointer" }}
                            >
                                <Col span={24}>
                                    <ImportedPortfolioCard name="Binance" />
                                </Col>
                            </Row>
                        </React.Fragment>
                    )}
                    {!showImportedPortfolios && !userInfo && !refreshing && (
                        <div style={{ marginTop: "20px", marginBottom: "8px" }}>
                            <LoginWall
                                compact
                                title="Sign in to connect Binance & cloud sync"
                                description="Sign in to track live Binance Spot/Earn/Funding balances, audit your USDT Ledger, use Ask AI, and back up manual portfolios to the cloud."
                                returnPath="/portfolios"
                            />
                        </div>
                    )}
                    {refreshing && (
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                marginTop: "20px",
                                marginBottom: "10px",
                            }}
                        >
                            <div
                                style={{
                                    color: Tokens.textSecondary,
                                    fontSize: "13px",
                                    fontWeight: 500,
                                }}
                            >
                                Loading portfolios
                            </div>
                            <Spin
                                indicator={
                                    <LoadingOutlined
                                        style={{
                                            fontSize: 16,
                                            color: Tokens.textSecondary,
                                        }}
                                        spin={refreshing}
                                    />
                                }
                            />
                        </div>
                    )}
                    {[1, 2, 3].map((index) => (
                        <Skeleton
                            loading={refreshing}
                            title
                            paragraph
                            active
                            key={index}
                            style={{ marginTop: 16 }}
                        />
                    ))}
                    {!refreshing && hasCombinedBalance && (
                        <div
                            data-testid="unified-net-worth-card"
                            style={{
                                backgroundColor: Tokens.card,
                                border: `1px solid ${Tokens.hairline}`,
                                borderRadius: Tokens.radiusCard,
                                padding: "20px",
                                marginTop: "16px",
                                marginBottom: "8px",
                                boxShadow: Tokens.topHighlight,
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: "8px",
                                    marginBottom: "6px",
                                }}
                            >
                                <span
                                    style={{
                                        color: Tokens.textSecondary,
                                        fontSize: "12px",
                                        fontWeight: 500,
                                    }}
                                >
                                    {(() => {
                                        if (
                                            binanceTotalValue > 0 &&
                                            hasManualBalance
                                        ) {
                                            return "Total net worth (Binance + Manual)";
                                        }
                                        if (binanceTotalValue > 0) {
                                            return "Total net worth";
                                        }
                                        return "Total manual balance";
                                    })()}
                                </span>
                                {binanceTotalValue > 0 && hasManualBalance && (
                                    <span
                                        style={{
                                            fontSize: "11px",
                                            color: Tokens.textMuted,
                                            fontFamily: Tokens.fontMono,
                                        }}
                                    >
                                        {`Binance ${currSymbol}${getFormattedAmount(
                                            binanceTotalValue,
                                            true,
                                            2
                                        )} · Manual ${currSymbol}${getFormattedAmount(
                                            headerStats.currentValue,
                                            true,
                                            2
                                        )}`}
                                    </span>
                                )}
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    flexWrap: "wrap",
                                    gap: "8px",
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        flexWrap: "wrap",
                                    }}
                                >
                                    <span
                                        style={{
                                            color: Tokens.textPrimary,
                                            fontSize: "28px",
                                            fontWeight: 600,
                                            fontFamily: Tokens.fontMono,
                                            fontVariantNumeric: "tabular-nums",
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        {currSymbol}
                                        {getFormattedAmount(
                                            combinedValue,
                                            true,
                                            2
                                        )}
                                    </span>
                                    {PerformanceTag(combinedPlPct, true)}
                                </div>
                                <button
                                    type="button"
                                    data-testid="overview-audit-ledger-btn"
                                    onClick={() =>
                                        history.push(
                                            "/imported/binance/report-card?view=ledger"
                                        )
                                    }
                                    style={{
                                        padding: "4px 10px",
                                        borderRadius: Tokens.radiusTag,
                                        border: `1px solid ${Tokens.hairline}`,
                                        backgroundColor: Tokens.surface,
                                        color: Tokens.accent,
                                        fontSize: "11px",
                                        fontWeight: 500,
                                        fontFamily: Tokens.fontMono,
                                        cursor: "pointer",
                                    }}
                                >
                                    Audit Ledger →
                                </button>
                            </div>
                            <div
                                style={{
                                    marginTop: "14px",
                                    paddingTop: "12px",
                                    borderTop: `1px solid ${Tokens.hairline}`,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    flexWrap: "wrap",
                                    gap: "12px",
                                    fontSize: "13px",
                                    fontVariantNumeric: "tabular-nums",
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "16px",
                                        flexWrap: "wrap",
                                    }}
                                >
                                    <span
                                        style={{
                                            color: Tokens.textSecondary,
                                            fontFamily: Tokens.fontMono,
                                            fontVariantNumeric: "tabular-nums",
                                        }}
                                    >
                                        Invested:{" "}
                                        <strong
                                            style={{
                                                color: Tokens.textPrimary,
                                                fontWeight: 500,
                                            }}
                                        >
                                            {currSymbol}
                                            {getFormattedAmount(
                                                combinedInvested,
                                                true,
                                                2
                                            )}
                                        </strong>
                                    </span>
                                    <span
                                        style={{
                                            color: Tokens.textSecondary,
                                            fontFamily: Tokens.fontMono,
                                            fontVariantNumeric: "tabular-nums",
                                        }}
                                    >
                                        Net P&amp;L:{" "}
                                        <strong
                                            style={{
                                                color:
                                                    combinedNetPl >= 0
                                                        ? Tokens.positive
                                                        : Tokens.negative,
                                                fontWeight: 500,
                                            }}
                                        >
                                            {combinedNetPl >= 0 ? "+" : "-"}
                                            {currSymbol}
                                            {getFormattedAmount(
                                                Math.abs(combinedNetPl),
                                                true,
                                                2
                                            )}
                                        </strong>
                                    </span>
                                </div>
                                {hasManualBalance && (
                                    <Button
                                        size="small"
                                        type="default"
                                        icon={
                                            <BarChartOutlined
                                                style={{ color: Tokens.accent }}
                                            />
                                        }
                                        onClick={() => {
                                            mpTapInvestmentListViewCTA();
                                            getTotalInvestmentList();
                                            setShowInvestmentsDrawer(true);
                                        }}
                                        style={{
                                            backgroundColor: Tokens.raised,
                                            color: Tokens.textPrimary,
                                            border: `1px solid ${Tokens.hairlineStrong}`,
                                            borderRadius: Tokens.radiusControl,
                                            fontWeight: 500,
                                        }}
                                    >
                                        Breakdown
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}

                    {!refreshing && (
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "10px",
                                marginTop: "20px",
                            }}
                        >
                            <span
                                style={{
                                    fontSize: "13px",
                                    fontWeight: 500,
                                    color: Tokens.textSecondary,
                                }}
                            >
                                Portfolios
                            </span>
                        </div>
                    )}

                    {!refreshing &&
                        allManualPortfolios.length > 0 &&
                        allManualPortfolios.map(
                            (
                                {
                                    name,
                                    uid,
                                    avatar,
                                    currentValue,
                                    totalInvestment,
                                    avgPrice,
                                    pl,
                                    activeCount,
                                    exitedCount,
                                    assetSummary,
                                },
                                index
                            ) => (
                                <OverviewCard
                                    itemInfo={{
                                        name,
                                        uid,
                                        avatar,
                                        handleMoreClk,
                                    }}
                                    itemData={{
                                        currentValue,
                                        totalInvestment,
                                        avgPrice,
                                        netPl:
                                            (Number(currentValue) || 0) -
                                            (Number(totalInvestment) || 0),
                                        pl,
                                        activeCount,
                                        exitedCount,
                                        assetSummary,
                                    }}
                                    currency={displayCurrency}
                                    index={index}
                                    key={uid}
                                />
                            )
                        )}

                    {!refreshing && allManualPortfolios.length === 0 && (
                        <div
                            style={{
                                backgroundColor: Tokens.card,
                                border: `1px solid ${Tokens.hairline}`,
                                borderRadius: Tokens.radiusCard,
                                padding: "32px 24px",
                                textAlign: "center",
                            }}
                        >
                            <div
                                style={{
                                    color: Tokens.textPrimary,
                                    fontSize: "16px",
                                    fontWeight: 500,
                                    marginBottom: "8px",
                                }}
                            >
                                No manual portfolios yet
                            </div>
                            <div
                                style={{
                                    color: Tokens.textSecondary,
                                    fontSize: "14px",
                                }}
                            >
                                Create a portfolio to track custom holdings
                                alongside your connected exchanges.
                            </div>
                        </div>
                    )}
                </div>
            </AppShell>
            <InvestmentList
                headerStats={headerStats}
                investments={investments}
                onClose={handleInvestmentListDrawer}
                visible={showInvestmentsDrawer}
                refreshing={refreshing}
                getAllPortfolioAssetsInfo={getAllPortfolioAssetsInfo}
                currency={displayCurrency}
            />
            <AddPortfolio
                showAddPortfolioDrawer={showAddPortfolioDrawer}
                onClose={setShowAddPortfolioDrawer}
            />
            <Settings
                visible={showSettings}
                onClose={() => setShowSettings(false)}
            />
        </div>
    );
};

export default Portfolios;
